import { useRef, useState } from 'react';
import type { DemoKey, DemoScript, FingerKey, Fx, Level, RoundProps, ViewProps } from '../kit.js';
import { BAD, BOARD, BoardButton, BoardFrame, INK, RADIUS, STROKE, TYPE, alpha, easier, mono, q, sans, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import { ERA_SKIN } from '../../art.js';
import type { Lang } from '../../../ui/text.js';

/**
 * SIGNAL OFFICER · CRACK THE CODE — a word comes in as symbols; the codebook turns them into letters.
 *
 * The intercepted message sits at the top, one symbol per letter, the one to crack now outlined;
 * below it the codebook (symbol = letter), and along the bottom a keypad of the codebook's letters,
 * in alphabetical order, as the kit's buttons. Tap the letters to spell the word, symbol by symbol.
 * A wrong letter is the mistake: it shows in red under the symbol for a moment, and the slot waits
 * for the right one (the same wrong key again while it shows is not another mistake). Round 1 is a
 * three-letter word with five symbols in the book; round 2 three letters and six symbols; round 3
 * four letters and six symbols. (A book that leaves one of the word's letters out — it is the one
 * keypad letter the book doesn't show — is still in the rules; no round deals it since 6 Oct 2026.)
 *
 * A converted board (kit.tsx): the rules as plain functions, a small view state (`CodeView`), a
 * pure `View`, the `Board` that plays it, and the `demo`.
 */

export interface CodeSpec {
  letters: number;
  /** How many symbols the codebook holds (the word's letters and some that are not in it). */
  keySize: number;
  /** How many of the word's letters the book leaves out. */
  hidden: number;
}

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const CODES: readonly CodeSpec[] = easier(
  [
    { letters: 3, keySize: 5, hidden: 0 },
    { letters: 4, keySize: 6, hidden: 0 },
  ], { keySize: 6 },
);

/** Short, plain words from the voyage, by length (three, four and five letters): no names, no brands. */
export const CODE_WORDS: Record<Lang, readonly (readonly string[])[]> = {
  en: [
    ['SUN', 'SKY', 'SEA', 'AIR', 'MAP', 'KEY', 'WAY', 'ICE', 'DAY', 'OAK'],
    ['HOME', 'SAFE', 'SHIP', 'STAR', 'MOON', 'GATE', 'SEED', 'CREW', 'LAND', 'RAIN', 'WIND', 'HOPE'],
    ['STARS', 'EARTH', 'WATER', 'LIGHT', 'OCEAN', 'RIVER', 'NORTH', 'SHORE', 'GREEN', 'PEACE', 'CLEAR', 'TRUST'],
  ],
  no: [
    ['SOL', 'HAV', 'LYS', 'VEI', 'DAG', 'ELV', 'SKY', 'TRE', 'FYR', 'ILD'],
    ['HJEM', 'NORD', 'SKIP', 'LAND', 'REGN', 'VIND', 'KART', 'PORT', 'VANN', 'SAND', 'FRED', 'KURS'],
    ['TRYGG', 'HAVET', 'LYSET', 'SOLEN', 'JORDA', 'ELVEN', 'FJELL', 'HAGEN', 'SKYEN', 'VEIEN'],
  ],
};

/** Letters the codebook may add that are not in the word. */
const FILLERS: Record<Lang, string> = { en: 'ABCDEFGHIKLMNOPRSTUWY', no: 'ABDEFGHIKLMNOPRSTUVY' };

export interface CodeEntry {
  glyph: number;
  letter: string;
  /** Left out of the book: shown as ?. */
  hidden: boolean;
}

export interface CodeDeal {
  word: string;
  /** The message: one symbol per letter of the word. */
  glyphs: number[];
  /** The codebook, in no particular order. */
  key: CodeEntry[];
  /** The keypad: the codebook's letters, alphabetically. */
  keypad: string[];
}

function shuffle<T>(list: T[], rand: () => number): T[] {
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1)) % (i + 1);
    [list[i], list[j]] = [list[j]!, list[i]!];
  }
  return list;
}

/** A round's message, codebook and keypad. */
export function dealCode(level: Level, lang: Lang, rand: () => number): CodeDeal {
  const spec = CODES[level]!;
  const words = CODE_WORDS[lang][spec.letters - 3]!;
  const word = words[Math.floor(rand() * words.length) % words.length]!;
  const letters = [...new Set(word)];
  const fillers = shuffle([...FILLERS[lang]].filter((c) => !letters.includes(c)), rand);
  const keyLetters = [...letters, ...fillers.slice(0, Math.max(0, spec.keySize - letters.length))];
  const glyphs = shuffle(Array.from({ length: GLYPHS }, (_, i) => i), rand);
  const glyphOf = new Map(keyLetters.map((l, i) => [l, glyphs[i]!] as const));
  const hidden = new Set(shuffle(letters.slice(), rand).slice(0, spec.hidden));
  return {
    word,
    glyphs: [...word].map((l) => glyphOf.get(l)!),
    key: shuffle(
      keyLetters.map((letter) => ({ glyph: glyphOf.get(letter)!, letter, hidden: hidden.has(letter) })),
      rand,
    ),
    keypad: keyLetters.slice().sort(),
  };
}

/** The symbols, stroked in a 24 × 24 box: each a different outline, readable at a glance. */
const GLYPHS = 12;

export function Glyph({ id, colour, width = 2.2 }: { id: number; colour: string; width?: number }) {
  const s = { fill: 'none', stroke: colour, strokeWidth: width, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (id % GLYPHS) {
    case 0:
      return (
        <g>
          <circle cx="12" cy="12" r="7.5" {...s} />
          <circle cx="12" cy="12" r="2" fill={colour} />
        </g>
      );
    case 1:
      return <path d="M12 4 L20.5 19 L3.5 19 Z" {...s} />;
    case 2:
      return <path d="M12 3 L20.5 12 L12 21 L3.5 12 Z" {...s} />;
    case 3:
      return <path d="M2.5 12 Q7.25 3 12 12 T21.5 12" {...s} />;
    case 4:
      return <path d="M12 3.5 V20.5 M3.5 12 H20.5" {...s} />;
    case 5:
      return <path d="M5 5 L19 19 M19 5 L5 19" {...s} />;
    case 6:
      return <path d="M4 12 L12 5 L20 12 M4 19 L12 12 L20 19" {...s} />;
    case 7:
      return <path d="M3.5 17 A8.5 8.5 0 0 1 20.5 17 Z" {...s} />;
    case 8:
      return (
        <g>
          <circle cx="12" cy="12" r="7.5" {...s} />
          <path d="M4.5 12 H19.5" {...s} />
        </g>
      );
    case 9:
      return <path d="M4 6 H20 M4 12 H20 M4 18 H20" {...s} />;
    case 10:
      return <path d="M12 20.5 V4 M5.5 10.5 L12 4 L18.5 10.5" {...s} />;
    default:
      return (
        <g>
          <path d="M2.5 12 Q12 3 21.5 12 Q12 21 2.5 12 Z" {...s} />
          <circle cx="12" cy="12" r="2.4" fill={colour} />
        </g>
      );
  }
}

/** A symbol `size` units across, its top left at (x, y). */
function GlyphAt({ id, colour, x, y, size }: { id: number; colour: string; x: number; y: number; size: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${size / 24})`}>
      <Glyph id={id} colour={colour} width={2.2 * Math.min(1.25, 24 / size)} />
    </g>
  );
}

/** A wrong letter shows for this long, and the same wrong key again within it is not another mistake. */
const WRONG_MS = 600;
/** A key shows pressed this long. */
const PRESS_MS = 160;

/** Everything the radio desk shows. */
export interface CodeView {
  /** The game clock, ms. */
  t: number;
  /** The message: a symbol for each letter of the word. */
  gl: readonly number[];
  /** The codebook in its order: its letters, and each one's symbol. */
  kl: string;
  kg: readonly number[];
  /** The book's letters that it leaves out (shown as ?). */
  hid: string;
  /** The word, as far as it is cracked. */
  so: string;
  /** A wrong letter while it shows: which, and how long ago (ms). */
  x: readonly [letter: string, age: number] | null;
  /** The key under the finger now, or ''. */
  p: string;
  fx?: Fx | undefined;
}

const W = BOARD.w;
const H = BOARD.h;
/** The message panel at the top, the codebook under it, and the keypad along the bottom. */
const MSG = { x: 10, y: 8, w: W - 20, h: 96 } as const;
const ENTRY_GAP = 5;
const KEY_GAP = 6;
/** One row of up to five; six go in two rows of three. */
const cols = (count: number) => (count <= 5 ? count : Math.ceil(count / 2));
const rowsOf = (count: number) => Math.ceil(count / cols(count));

/**
 * The desk for a codebook of `count`: one row of keys stands taller than two, and the codebook
 * sits halfway between the message and the keypad, so a short book leaves no hole.
 */
export function deskFor(count: number) {
  const rows = rowsOf(count);
  const entryH = rows === 1 ? 36 : 30;
  const keyH = rows === 1 ? 60 : 46;
  const bookH = 20 + rows * entryH + (rows - 1) * ENTRY_GAP + 6;
  const padH = rows * keyH + (rows - 1) * KEY_GAP;
  const gap = (H - 8 - MSG.y - MSG.h - bookH - padH) / 2;
  const bookY = Math.round(MSG.y + MSG.h + gap);
  return { rows, entryH, keyH, bookY, bookH, padY: H - 8 - padH };
}

/** Where codebook entry `i` of `count` sits: its box. */
export function bookEntry(i: number, count: number) {
  const c = cols(count);
  const { entryH, bookY } = deskFor(count);
  const w = (W - 36 - (c - 1) * ENTRY_GAP) / c;
  return { x: 18 + (i % c) * (w + ENTRY_GAP), y: bookY + 20 + Math.floor(i / c) * (entryH + ENTRY_GAP), w, h: entryH };
}

/** Where keypad key `i` of `count` sits: its box. */
export function keyBox(i: number, count: number) {
  const c = cols(count);
  const { keyH, padY } = deskFor(count);
  const w = (W - 20 - (c - 1) * 8) / c;
  return { x: 10 + (i % c) * (w + 8), y: padY + Math.floor(i / c) * (keyH + KEY_GAP), w, h: keyH };
}

/** The desk, from its view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<CodeView>) {
  const skin = ERA_SKIN[era];
  const id = useSvgId();
  const n = view.gl.length;
  const filled = view.so.length;
  const done = filled >= n;
  const wrong = view.x && view.x[1] < WRONG_MS && !done ? view.x[0] : null;
  const book = [...view.kl];
  const keypad = book.slice().sort();
  const hidden = view.hid.length > 0;
  const status = done
    ? { text: say(lang, '✓ CODE CRACKED', '✓ KODEN KNEKT'), tone: 'ok' as const }
    : wrong
      ? { text: say(lang, 'NOT THAT LETTER', 'IKKE DEN BOKSTAVEN'), tone: 'bad' as const }
      : { text: say(lang, 'LETTERS', 'BOKSTAVER'), count: [filled, n] as const, hint: say(lang, 'USE THE CODEBOOK', 'SE I KODEBOKEN') };
  const press = input?.press;

  // The message's tiles: a symbol box with its letter's slot under it, centred across the panel.
  const tile = n >= 5 ? 50 : 56;
  const boxY = MSG.y + 22;
  const boxH = tile - 10;
  const left = W / 2 - (n * tile + (n - 1) * 8) / 2;
  const glyphSize = tile - 24;
  const letterY = boxY + boxH + 24;
  // The slot waiting for a letter bobs a little, slowly.
  const bob = Math.sin((view.t / 1600) * Math.PI * 2) * 1.2;

  const { bookY, bookH } = deskFor(book.length);

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <pattern id={`${id}l`} width="4" height="4" patternUnits="userSpaceOnUse">
          <rect width="4" height="2" fill={INK.hi} opacity=".03" />
        </pattern>
      </defs>

      {/* the message, as it came in */}
      <rect x={MSG.x} y={MSG.y} width={MSG.w} height={MSG.h} rx={RADIUS.button + 2} fill={alpha(INK.onFill, 0.55)} stroke={alpha(skin.ink, 0.25)} strokeWidth={STROKE.hair} />
      <rect x={MSG.x} y={MSG.y} width={MSG.w} height={MSG.h} rx={RADIUS.button + 2} fill={`url(#${id}l)`} />
      <text x={MSG.x + 10} y={MSG.y + 15} fontFamily={mono} fontSize={TYPE.hint} letterSpacing="1.6" fill={skin.haze} opacity=".6">
        {say(lang, 'INTERCEPTED', 'OPPFANGET')}
      </text>
      {view.gl.map((g, i) => {
        const x = left + i * (tile + 8);
        const now = i === filled && !done;
        const solved = i < filled;
        const bad = now && wrong;
        return (
          <g key={i}>
            <rect
              x={x}
              y={boxY}
              width={tile}
              height={boxH}
              rx="9"
              fill={now ? alpha(accent, 0.12) : alpha(skin.ink, 0.06)}
              stroke={bad ? BAD : now ? accent : alpha(skin.ink, 0.25)}
              strokeWidth={now ? STROKE.line : STROKE.hair + 0.5}
            />
            <GlyphAt id={Math.round(g)} colour={now ? INK.hi : solved ? alpha(skin.haze, 0.55) : skin.haze} x={x + (tile - glyphSize) / 2} y={boxY + (boxH - glyphSize) / 2} size={glyphSize} />
            {solved ? (
              <text x={x + tile / 2} y={letterY} textAnchor="middle" fontFamily={sans} fontSize={TYPE.big} fontWeight="700" fill={INK.hi}>
                {view.so[i]}
              </text>
            ) : bad ? (
              <text x={x + tile / 2} y={letterY} textAnchor="middle" fontFamily={sans} fontSize={TYPE.big} fontWeight="700" fill={BAD}>
                {wrong}
              </text>
            ) : (
              <rect x={x + tile * 0.22} y={letterY - 3 + (now ? bob : 0)} width={tile * 0.56} height="3" rx="1.5" fill={now ? accent : alpha(INK.hi, 0.22)} />
            )}
          </g>
        );
      })}

      {/* the codebook */}
      <rect x="10" y={bookY} width={W - 20} height={bookH} rx={RADIUS.button + 2} fill={alpha(INK.onFill, 0.7)} stroke={alpha(skin.haze, 0.27)} strokeWidth={STROKE.hair} strokeDasharray="4 3" />
      <text x="20" y={bookY + 14} fontFamily={mono} fontSize={TYPE.hint} letterSpacing="1.4" fill={skin.haze} opacity=".6">
        {say(lang, 'CODEBOOK', 'KODEBOK')}
      </text>
      {hidden && (
        <text x={W - 20} y={bookY + 14} textAnchor="end" fontFamily={mono} fontSize={TYPE.hint} letterSpacing="1" fill={accent}>
          {say(lang, '? = THE LETTER LEFT OVER', '? = BOKSTAVEN TIL OVERS')}
        </text>
      )}
      {book.map((letter, i) => {
        const box = bookEntry(i, book.length);
        const cx = box.x + box.w / 2;
        const cy = box.y + box.h / 2;
        const missing = view.hid.includes(letter);
        return (
          <g key={`${letter}${i}`}>
            <rect x={box.x} y={box.y} width={box.w} height={box.h} rx="7" fill={alpha(INK.onFill, 0.35)} stroke={alpha(INK.hi, 0.07)} />
            <GlyphAt id={Math.round(view.kg[i] ?? 0)} colour={skin.haze} x={cx - 25} y={cy - 10} size={20} />
            <text x={cx} y={cy + 4} textAnchor="middle" fontFamily={mono} fontSize={TYPE.hint} fill={INK.faint}>
              =
            </text>
            <text x={cx + 15} y={cy + 8.5} textAnchor="middle" fontFamily={sans} fontSize={TYPE.big} fontWeight="700" fill={missing ? accent : INK.hi}>
              {missing ? '?' : letter}
            </text>
          </g>
        );
      })}

      {/* the keypad */}
      {keypad.map((letter, i) => {
        const box = keyBox(i, keypad.length);
        const isWrong = wrong === letter;
        return (
          <BoardButton
            key={letter}
            x={box.x}
            y={box.y}
            w={box.w}
            h={box.h}
            label={letter}
            accent={accent}
            tone={isWrong ? 'bad' : 'plain'}
            down={view.p === letter}
            onPress={press && !done ? () => press(i) : undefined}
          >
            <text y={TYPE.big * 0.36} textAnchor="middle" fontFamily={sans} fontSize={TYPE.big} fontWeight="700" fill={isWrong ? BAD : INK.hi}>
              {letter}
            </text>
          </BoardButton>
        );
      })}
    </BoardFrame>
  );
}

function Board(props: RoundProps) {
  const { level, lang } = props;
  const [deal] = useState(() => dealCode(level, lang, seeded(Date.now() + level * 2_750_159)));
  const filled = useRef(0);
  const wrong = useRef<{ letter: string; at: number } | null>(null);
  const pressed = useRef<{ letter: string; at: number } | null>(null);
  const round = useRound(props);

  const press = (i: number) => {
    const letter = deal.keypad[i];
    const at = filled.current;
    if (!round.live || letter === undefined || at >= deal.word.length) return;
    const now = round.time();
    pressed.current = { letter, at: now };
    if (deal.word[at] === letter) {
      filled.current = at + 1;
      wrong.current = null;
      round.hit();
      round.progress((at + 1) / deal.word.length);
      if (at + 1 >= deal.word.length) round.win();
      return;
    }
    // A second tap on the same wrong key while it still shows red is the same mistake.
    if (wrong.current && wrong.current.letter === letter && now - wrong.current.at < WRONG_MS) return;
    wrong.current = { letter, at: now };
    round.miss();
  };

  const now = round.now;
  const view: CodeView = {
    t: q(now),
    gl: deal.glyphs,
    kl: deal.key.map((e) => e.letter).join(''),
    kg: deal.key.map((e) => e.glyph),
    hid: deal.key.filter((e) => e.hidden).map((e) => e.letter).join(''),
    so: deal.word.slice(0, filled.current),
    x: wrong.current && now - wrong.current.at < WRONG_MS ? [wrong.current.letter, q(now - wrong.current.at)] : null,
    p: pressed.current && now - pressed.current.at < PRESS_MS ? pressed.current.letter : '',
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={props.era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Tap the letters in order', 'Trykk bokstavene i rekkefølge'), press }}
    />
  );
}

/**
 * The how-to's round 1: SKY (a word in both languages), its codebook of five in the book's order,
 * and the symbol for each letter.
 */
const DEMO_WORD = 'SKY';
const DEMO_BOOK = 'KASEY';
const DEMO_SYMBOLS = [2, 5, 0, 8, 3] as const;
const symbolOf = (letter: string) => DEMO_SYMBOLS[DEMO_BOOK.indexOf(letter)]!;
/** When the finger taps each letter. */
const DEMO_TAPS = [2_000, 3_200, 4_400] as const;

/**
 * The how-to: the finger points at the first symbol, finds it in the codebook and taps its letter;
 * then the next two the same way, and the word is cracked.
 */
function demoCode(): DemoScript<CodeView> {
  const keypad = [...DEMO_BOOK].sort();
  const keys: DemoKey<CodeView>[] = [
    { at: 0, view: { t: 0, gl: [...DEMO_WORD].map(symbolOf), kl: DEMO_BOOK, kg: DEMO_SYMBOLS, hid: '', so: '', x: null, p: '' } },
  ];
  const finger: FingerKey[] = [
    { at: 0, x: 290, y: 300, hide: true },
    { at: 400, x: 290, y: 300, hide: false },
  ];
  // First the symbol itself, in the message.
  const tile = 56;
  const first = W / 2 - (DEMO_WORD.length * tile + (DEMO_WORD.length - 1) * 8) / 2 + tile / 2;
  finger.push({ at: 700, x: Math.round(first), y: MSG.y + 46 }, { at: 1_000, x: Math.round(first), y: MSG.y + 46 });
  DEMO_TAPS.forEach((at, k) => {
    const letter = DEMO_WORD[k]!;
    const entry = bookEntry(DEMO_BOOK.indexOf(letter), DEMO_BOOK.length);
    const key = keyBox(keypad.indexOf(letter), keypad.length);
    // On the entry's symbol, so the letter beside it stays in sight.
    const ex = Math.round(entry.x + entry.w / 2 - 15);
    const ey = Math.round(entry.y + entry.h / 2);
    const kx = Math.round(key.x + key.w / 2);
    const ky = Math.round(key.y + key.h / 2);
    finger.push(
      { at: at - 650, x: ex, y: ey },
      { at: at - 350, x: ex, y: ey },
      { at, x: kx, y: ky, down: true },
      { at: at + PRESS_MS, x: kx, y: ky, down: false },
    );
    keys.push(
      { at, view: { so: DEMO_WORD.slice(0, k + 1), p: letter, fx: [1, 0] }, ease: 'step' },
      { at: at + PRESS_MS, view: { fx: [0.64, 0] } },
      { at: at + PRESS_MS + 1, view: { p: '' }, ease: 'step' },
      { at: at + 450, view: { fx: [0, 0] } },
    );
  });
  finger.push({ at: 5_400, x: 290, y: 300, hide: true });
  return {
    ms: 7_000,
    keys,
    finger,
    steps: [
      { at: 0, en: 'FIND THE SYMBOL IN THE BOOK', no: 'FINN SYMBOLET I KODEBOKEN' },
      { at: 1_700, en: 'TAP THE LETTER IT STANDS FOR', no: 'TRYKK BOKSTAVEN DET STÅR FOR' },
      { at: 2_500, en: 'THEN THE NEXT SYMBOL', no: 'SÅ NESTE SYMBOL' },
    ],
  };
}

export const game = thirtyGame<CodeView>({
  Board,
  View,
  demo: demoCode(),
  words: {
    en: {
      instruction: 'Use the codebook to turn the symbols into letters. Tap them in order to spell the word.',
      howMove: 'Find each symbol in the codebook. Tap its letter.',
    },
    no: {
      instruction: 'Bruk kodeboken til å gjøre symbolene om til bokstaver. Trykk dem i rekkefølge.',
      howMove: 'Finn hvert symbol i kodeboken. Trykk bokstaven det står for.',
    },
  },
});
